/**
 * NotificationsConfigTab Component
 * Configure notification rules and daily summary
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, Form, Badge, Button, Modal, Alert, Spinner } from 'react-bootstrap';
import {
  IconBell,
  IconBrandWhatsapp,
  IconMail,
  IconCheck,
  IconEdit,
  IconTrash,
  IconPlus,
  IconInfoCircle,
  IconCopy,
  IconClock,
  IconMoodSmile,
  IconX,
} from '@tabler/icons-react';
import Swal from 'sweetalert2';
import NumberFormat from 'react-number-format';
import EmojiPickerWrapper from '../../../../jsx/components/EmojiPIcker';
import { useAgentsFlowApi } from '../../hooks/useAgentsFlowApi';
import { NOTIFICATION_CHANNELS, NOTIFICATION_VARIABLES, NOTIFICATION_EVENTS } from '../../constants';
import ProBadge from '../ProBadge';
import UpgradeModal from '../UpgradeModal';
import useProFeature from '../../hooks/useProFeature';
import './NotificationsConfigTab.css';

const MAX_PHONES = 3;

// ===============================================
// CHANNEL ICONS
// ===============================================

const CHANNEL_ICONS = {
  whatsapp: IconBrandWhatsapp,
  email: IconMail,
};

// ===============================================
// HELPERS: owner_phone <-> phones array
// ===============================================

/** "5511999999999,5511888888888" -> ["5511999999999","5511888888888"] */
const phonesFromString = (str) => {
  if (!str) return [''];
  const phones = str.split(',').map((p) => p.trim()).filter(Boolean);
  return phones.length > 0 ? phones : [''];
};

/** ["5511999999999","5511888888888"] -> "5511999999999,5511888888888" */
const phonesToString = (arr) => arr.filter(Boolean).join(',');

// ===============================================
// RULE CARD
// ===============================================

const RuleCard = ({ rule, onEdit, onDelete, onToggle }) => {
  return (
    <Card className={`rule-card ${rule.is_enabled ? 'enabled' : ''}`}>
      <Card.Body>
        <div className="rule-header">
          <div className="rule-icon">
            <IconBell size={20} />
          </div>
          <div className="rule-info">
            <h6 className="rule-name">{rule.name}</h6>
            <p className="rule-trigger">
              {rule.trigger_event_display || rule.trigger_description || 'Sem evento definido'}
            </p>
          </div>
          <Form.Check
            type="switch"
            id={`rule-${rule.id}`}
            checked={rule.is_enabled}
            onChange={(e) => onToggle(rule, e.target.checked)}
          />
        </div>

        <div className="rule-actions">
          <div className="channel-badges">
            {(rule.notification_channels || []).map((ch) => {
              const ChannelIcon = CHANNEL_ICONS[ch];
              return (
                <Badge key={ch} bg="secondary" className="channel-badge">
                  {ChannelIcon && <ChannelIcon size={12} className="me-1" />}
                  {ch}
                </Badge>
              );
            })}
            {(!rule.notification_channels || rule.notification_channels.length === 0) && (
              <span className="text-muted small">Nenhum canal</span>
            )}
          </div>
          <div className="d-flex gap-2">
            <Button variant="outline-primary" size="sm" onClick={() => onEdit(rule)}>
              <IconEdit size={14} className="me-1" />
              Editar
            </Button>
            <Button variant="outline-danger" size="sm" onClick={() => onDelete(rule)}>
              <IconTrash size={14} />
            </Button>
          </div>
        </div>
      </Card.Body>
    </Card>
  );
};

// ===============================================
// RULE EDITOR MODAL
// ===============================================

const RuleEditorModal = ({ show, onHide, rule, onSave }) => {
  const [form, setForm] = useState({
    name: '',
    trigger_event: '',
    message_template: '',
    notification_channels: ['whatsapp'],
    phones: [''],
    owner_email: '',
    is_enabled: true,
  });

  // Emoji picker state
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const templateRef = useRef(null);
  const emojiPickerRef = useRef(null);

  useEffect(() => {
    if (rule) {
      setForm({
        name: rule.name || '',
        trigger_event: rule.trigger_event || '',
        message_template: rule.message_template || '',
        notification_channels: rule.notification_channels || ['whatsapp'],
        phones: phonesFromString(rule.owner_phone),
        owner_email: rule.owner_email || '',
        is_enabled: rule.is_enabled ?? true,
      });
    } else {
      setForm({
        name: '',
        trigger_event: '',
        message_template: '',
        notification_channels: ['whatsapp'],
        phones: [''],
        owner_email: '',
        is_enabled: true,
      });
    }
    setShowEmojiPicker(false);
  }, [rule, show]);

  const handleChannelToggle = (channel) => {
    setForm((prev) => ({
      ...prev,
      notification_channels: prev.notification_channels.includes(channel)
        ? prev.notification_channels.filter((c) => c !== channel)
        : [...prev.notification_channels, channel],
    }));
  };

  // --- Phone management ---
  const handlePhoneChange = (index, rawValue) => {
    setForm((prev) => {
      const newPhones = [...prev.phones];
      newPhones[index] = rawValue;
      return { ...prev, phones: newPhones };
    });
  };

  const addPhone = () => {
    setForm((prev) => {
      if (prev.phones.length >= MAX_PHONES) return prev;
      return { ...prev, phones: [...prev.phones, ''] };
    });
  };

  const removePhone = (index) => {
    setForm((prev) => {
      const newPhones = prev.phones.filter((_, i) => i !== index);
      return { ...prev, phones: newPhones.length > 0 ? newPhones : [''] };
    });
  };

  // --- Template variable insertion ---
  const insertVariable = (variable) => {
    const textarea = templateRef.current;
    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const text = form.message_template;
      const newText = text.substring(0, start) + `{${variable}}` + text.substring(end);
      const newCursorPos = start + variable.length + 2;
      setForm((prev) => ({ ...prev, message_template: newText }));
      setTimeout(() => {
        if (templateRef.current) {
          templateRef.current.focus();
          templateRef.current.setSelectionRange(newCursorPos, newCursorPos);
        }
      }, 0);
    }
  };

  // --- Emoji picker ---
  const handleEmojiSelect = (emoji) => {
    const textarea = templateRef.current;
    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const text = form.message_template;
      const newText = text.substring(0, start) + emoji + text.substring(end);
      const newCursorPos = start + emoji.length;
      setForm((prev) => ({ ...prev, message_template: newText }));
      setTimeout(() => {
        if (templateRef.current) {
          templateRef.current.value = newText;
          templateRef.current.focus();
          templateRef.current.setSelectionRange(newCursorPos, newCursorPos);
        }
      }, 0);
    }
  };

  // Get variables for selected event
  const selectedEvent = NOTIFICATION_EVENTS.find((e) => e.id === form.trigger_event);
  const eventVariables = selectedEvent
    ? NOTIFICATION_VARIABLES.filter((v) => selectedEvent.variables.includes(v.id))
    : NOTIFICATION_VARIABLES;

  const handleSave = () => {
    if (!form.trigger_event) {
      Swal.fire({ icon: 'warning', title: 'Evento obrigatório', text: 'Selecione o evento que dispara a notificação.' });
      return;
    }
    // Build payload with owner_phone as comma-separated string
    const payload = {
      ...form,
      owner_phone: phonesToString(form.phones),
    };
    delete payload.phones;
    onSave(payload);
    onHide();
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          <IconBell size={24} className="me-2" />
          {rule ? 'Editar Regra' : 'Nova Regra de Notificação'}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {/* Evento */}
        <Form.Group className="mb-3">
          <Form.Label className="fw-bold">Evento *</Form.Label>
          {NOTIFICATION_EVENTS.map((event) => (
            <Form.Check
              key={event.id}
              type="radio"
              name="trigger_event"
              id={`event-${event.id}`}
              label={
                <span>
                  <strong>{event.label}</strong>
                  <span className="text-muted ms-2 small">{event.description}</span>
                </span>
              }
              value={event.id}
              checked={form.trigger_event === event.id}
              onChange={(e) => {
                const selected = NOTIFICATION_EVENTS.find((ev) => ev.id === e.target.value);
                setForm((prev) => ({
                  ...prev,
                  trigger_event: e.target.value,
                  name: selected.label,
                  message_template: prev.message_template || selected.defaultTemplate,
                }));
              }}
              className="mb-2"
            />
          ))}
          <Form.Text className="text-muted">
            A notificação será disparada automaticamente quando o evento ocorrer.
          </Form.Text>
        </Form.Group>

        {/* Nome (auto-preenchido) */}
        <Form.Group className="mb-3">
          <Form.Label className="fw-bold">Nome da regra</Form.Label>
          <Form.Control
            type="text"
            placeholder="Auto-preenchido pelo evento"
            value={form.name}
            onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
          />
        </Form.Group>

        {/* Channels */}
        <Form.Group className="mb-4">
          <Form.Label className="fw-bold">Canais de Notificação</Form.Label>
          <div className="d-flex gap-3">
            {NOTIFICATION_CHANNELS.map((channel) => {
              const ChannelIcon = CHANNEL_ICONS[channel.id];
              return (
                <Form.Check
                  key={channel.id}
                  type="checkbox"
                  id={`rule-channel-${channel.id}`}
                  label={
                    <span className="d-flex align-items-center gap-1">
                      {ChannelIcon && <ChannelIcon size={16} />}
                      {channel.name}
                    </span>
                  }
                  checked={form.notification_channels.includes(channel.id)}
                  onChange={() => handleChannelToggle(channel.id)}
                />
              );
            })}
          </div>
        </Form.Group>

        {/* Contact fields */}
        <div className="row mb-4">
          {form.notification_channels.includes('whatsapp') && (
            <div className="col-md-6">
              <Form.Group>
                <Form.Label className="fw-bold">
                  <IconBrandWhatsapp size={16} className="me-1" />
                  WhatsApp do Proprietário
                </Form.Label>
                {form.phones.map((phone, index) => (
                  <div key={index} className="d-flex align-items-center gap-2 mb-2">
                    <NumberFormat
                      format={phone.length >= 3 && phone[2] !== '9' ? "(##) ####-####" : "(##) #####-####"}
                      mask="_"
                      className="form-control"
                      placeholder="(11) 99999-9999"
                      value={phone}
                      onValueChange={(values) => handlePhoneChange(index, values.value)}
                    />
                    {form.phones.length > 1 && (
                      <Button
                        variant="outline-danger"
                        size="sm"
                        className="flex-shrink-0"
                        onClick={() => removePhone(index)}
                      >
                        <IconX size={14} />
                      </Button>
                    )}
                  </div>
                ))}
                {form.phones.length < MAX_PHONES && (
                  <Button variant="outline-secondary" size="sm" onClick={addPhone}>
                    <IconPlus size={14} className="me-1" />
                    Adicionar número ({form.phones.length}/{MAX_PHONES})
                  </Button>
                )}
                <Form.Text className="text-muted d-block mt-1">
                  Até {MAX_PHONES} números. Formato: (DDD) número
                </Form.Text>
              </Form.Group>
            </div>
          )}
          {form.notification_channels.includes('email') && (
            <div className="col-md-6">
              <Form.Group>
                <Form.Label className="fw-bold">
                  <IconMail size={16} className="me-1" />
                  Email do Proprietário
                </Form.Label>
                <Form.Control
                  type="email"
                  placeholder="email@empresa.com"
                  value={form.owner_email}
                  onChange={(e) => setForm((prev) => ({ ...prev, owner_email: e.target.value }))}
                />
              </Form.Group>
            </div>
          )}
        </div>

        {/* Template */}
        <Form.Group className="mb-3">
          <div className="d-flex justify-content-between align-items-center mb-1">
            <Form.Label className="fw-bold mb-0">Template da Mensagem</Form.Label>
            <Button
              variant="outline-secondary"
              size="sm"
              className="p-1"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setShowEmojiPicker((prev) => !prev);
              }}
              type="button"
            >
              <IconMoodSmile size={16} />
            </Button>
          </div>
          <div style={{ position: 'relative' }}>
            <Form.Control
              as="textarea"
              id="rule-template-textarea"
              rows={4}
              value={form.message_template}
              onChange={(e) => setForm((prev) => ({ ...prev, message_template: e.target.value }))}
              placeholder="Ex: Novo agendamento para {client_name} em {appointment_date} às {appointment_time}"
              ref={templateRef}
            />
            {showEmojiPicker && (
              <div
                ref={emojiPickerRef}
                style={{
                  position: 'absolute',
                  top: '100%',
                  right: 0,
                  zIndex: 1050,
                  marginTop: '5px',
                }}
              >
                <EmojiPickerWrapper
                  onEmojiSelect={handleEmojiSelect}
                  onClose={() => setShowEmojiPicker(false)}
                />
              </div>
            )}
          </div>
        </Form.Group>

        {/* Variables */}
        <div className="variables-section">
          <Form.Label className="text-muted small mb-2">
            <IconInfoCircle size={14} className="me-1" />
            {selectedEvent
              ? `Variáveis disponíveis para "${selectedEvent.label}" (clique para inserir):`
              : 'Selecione um evento para ver as variáveis disponíveis:'}
          </Form.Label>
          <div className="d-flex flex-wrap gap-1">
            {eventVariables.map((v) => (
              <Badge
                key={v.id}
                bg="light"
                text="dark"
                className="variable-badge"
                onClick={() => insertVariable(v.id)}
                style={{ cursor: 'pointer' }}
              >
                {`{${v.id}}`}
                <IconCopy size={10} className="ms-1" />
              </Badge>
            ))}
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Cancelar
        </Button>
        <Button variant="primary" onClick={handleSave}>
          <IconCheck size={18} className="me-1" />
          Salvar
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

// ===============================================
// DAILY SUMMARY SECTION
// ===============================================

const ModuleCheckboxGroup = ({ title, items, selected, onToggle }) => (
  <div className="module-group mb-3">
    <Form.Label className="fw-bold text-muted small text-uppercase">{title}</Form.Label>
    <div className="module-checkboxes">
      {items.map((item) => (
        <Form.Check
          key={item.id}
          type="checkbox"
          id={`module-${item.id}`}
          label={
            <span className="d-flex align-items-center gap-1">
              <span>{item.icon}</span>
              {item.label}
            </span>
          }
          checked={selected.includes(item.id)}
          onChange={() => onToggle(item.id)}
          className="module-checkbox-item"
        />
      ))}
    </div>
  </div>
);

const DailySummarySection = ({ config, onSave }) => {
  const { loadAvailableSummaryModules } = useAgentsFlowApi();
  const [form, setForm] = useState({
    is_enabled: false,
    send_time: '20:00',
    notification_channels: ['whatsapp'],
    owner_phone: '',
    owner_email: '',
    selected_modules: [],
    selected_performance_kpis: [],
  });
  const [availableModules, setAvailableModules] = useState(null);
  const [loadingModules, setLoadingModules] = useState(false);

  useEffect(() => {
    if (config) {
      setForm({
        is_enabled: config.is_enabled ?? false,
        send_time: config.send_time || '20:00',
        notification_channels: config.notification_channels || ['whatsapp'],
        owner_phone: config.owner_phone || '',
        owner_email: config.owner_email || '',
        selected_modules: config.selected_modules || [],
        selected_performance_kpis: config.selected_performance_kpis || [],
      });
    }
  }, [config]);

  // Carrega módulos disponíveis ao ativar
  useEffect(() => {
    if (form.is_enabled && !availableModules) {
      setLoadingModules(true);
      loadAvailableSummaryModules()
        .then((data) => setAvailableModules(data))
        .catch(() => {})
        .finally(() => setLoadingModules(false));
    }
  }, [form.is_enabled, availableModules, loadAvailableSummaryModules]);

  const handleChannelToggle = (channel) => {
    setForm((prev) => ({
      ...prev,
      notification_channels: prev.notification_channels.includes(channel)
        ? prev.notification_channels.filter((c) => c !== channel)
        : [...prev.notification_channels, channel],
    }));
  };

  const handleModuleToggle = (moduleId) => {
    setForm((prev) => ({
      ...prev,
      selected_modules: prev.selected_modules.includes(moduleId)
        ? prev.selected_modules.filter((m) => m !== moduleId)
        : [...prev.selected_modules, moduleId],
    }));
  };

  const handleKpiToggle = (kpiId) => {
    setForm((prev) => ({
      ...prev,
      selected_performance_kpis: prev.selected_performance_kpis.includes(kpiId)
        ? prev.selected_performance_kpis.filter((k) => k !== kpiId)
        : [...prev.selected_performance_kpis, kpiId],
    }));
  };

  const handleSave = () => {
    onSave(form);
  };

  return (
    <div className="daily-summary-section">
      <h5 className="mb-3">
        <IconClock size={20} className="me-2" />
        Resumo Diário
      </h5>
      <Card>
        <Card.Body>
          <Form.Check
            type="switch"
            id="daily-summary-toggle"
            label="Enviar resumo diário das atividades"
            checked={form.is_enabled}
            onChange={(e) => setForm((prev) => ({ ...prev, is_enabled: e.target.checked }))}
            className="mb-3"
          />

          {form.is_enabled && (
            <>
              <div className="row mb-3">
                <div className="col-md-4">
                  <Form.Group>
                    <Form.Label>Horário de envio</Form.Label>
                    <Form.Control
                      type="time"
                      value={form.send_time}
                      onChange={(e) => setForm((prev) => ({ ...prev, send_time: e.target.value }))}
                    />
                  </Form.Group>
                </div>
              </div>

              <Form.Group className="mb-3">
                <Form.Label className="fw-bold">Canais</Form.Label>
                <div className="d-flex gap-3">
                  {NOTIFICATION_CHANNELS.map((channel) => {
                    const ChannelIcon = CHANNEL_ICONS[channel.id];
                    return (
                      <Form.Check
                        key={channel.id}
                        type="checkbox"
                        id={`summary-channel-${channel.id}`}
                        label={
                          <span className="d-flex align-items-center gap-1">
                            {ChannelIcon && <ChannelIcon size={16} />}
                            {channel.name}
                          </span>
                        }
                        checked={form.notification_channels.includes(channel.id)}
                        onChange={() => handleChannelToggle(channel.id)}
                      />
                    );
                  })}
                </div>
              </Form.Group>

              <div className="row mb-3">
                {form.notification_channels.includes('whatsapp') && (
                  <div className="col-md-6">
                    <Form.Group>
                      <Form.Label>WhatsApp</Form.Label>
                      <Form.Control
                        type="tel"
                        placeholder="5511999999999"
                        value={form.owner_phone}
                        onChange={(e) => setForm((prev) => ({ ...prev, owner_phone: e.target.value }))}
                      />
                    </Form.Group>
                  </div>
                )}
                {form.notification_channels.includes('email') && (
                  <div className="col-md-6">
                    <Form.Group>
                      <Form.Label>Email</Form.Label>
                      <Form.Control
                        type="email"
                        placeholder="email@empresa.com"
                        value={form.owner_email}
                        onChange={(e) => setForm((prev) => ({ ...prev, owner_email: e.target.value }))}
                      />
                    </Form.Group>
                  </div>
                )}
              </div>

              <hr className="my-3" />

              {/* Seleção de Módulos de Resumo */}
              {loadingModules ? (
                <div className="text-center py-3">
                  <Spinner animation="border" size="sm" />
                </div>
              ) : availableModules ? (
                <>
                  <h6 className="mb-3">
                    <IconInfoCircle size={16} className="me-1" />
                    Dados do Dia
                  </h6>
                  <p className="text-muted small">
                    Selecione quais informações deseja receber no resumo diário.
                  </p>
                  {Object.entries(availableModules.modules?.by_category || {}).map(([category, items]) => (
                    <ModuleCheckboxGroup
                      key={category}
                      title={category}
                      items={items}
                      selected={form.selected_modules}
                      onToggle={handleModuleToggle}
                    />
                  ))}

                  <hr className="my-3" />

                  <h6 className="mb-3">
                    <IconInfoCircle size={16} className="me-1" />
                    Relatório de Desempenho
                  </h6>
                  <p className="text-muted small">
                    KPIs com análise comparativa (dia anterior, tendência semanal, média mensal).
                  </p>
                  {Object.entries(availableModules.performance_kpis?.by_category || {}).map(([category, items]) => (
                    <ModuleCheckboxGroup
                      key={category}
                      title={category}
                      items={items}
                      selected={form.selected_performance_kpis}
                      onToggle={handleKpiToggle}
                    />
                  ))}
                </>
              ) : null}

              {form.selected_modules.length === 0 && form.selected_performance_kpis.length === 0 && (
                <Alert variant="info" className="small mb-3">
                  <IconInfoCircle size={16} className="me-1" />
                  Nenhum módulo selecionado. O resumo será enviado com as métricas padrão (mensagens, agendamentos, contatos).
                </Alert>
              )}

              <Button variant="primary" size="sm" onClick={handleSave}>
                <IconCheck size={16} className="me-1" />
                Salvar Resumo Diário
              </Button>
            </>
          )}

          {!form.is_enabled && (
            <p className="text-muted small mb-0">
              Ative para receber um resumo diário com métricas de mensagens, agendamentos e contatos.
            </p>
          )}
        </Card.Body>
      </Card>
    </div>
  );
};

// ===============================================
// MAIN COMPONENT
// ===============================================

const NotificationsConfigTab = ({ agentId }) => {
  const {
    loadNotificationRules,
    createNotificationRule,
    updateNotificationRule,
    deleteNotificationRule,
    loadDailySummaryConfig,
    saveDailySummaryConfig,
  } = useAgentsFlowApi();
  const { isProPlan, upgradeModal, openUpgradeModal, closeUpgradeModal } = useProFeature();

  const [rules, setRules] = useState([]);
  const [dailySummary, setDailySummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingRule, setEditingRule] = useState(null);
  const [showModal, setShowModal] = useState(false);

  // Load data
  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [rulesData, summaryData] = await Promise.all([
          loadNotificationRules(agentId),
          loadDailySummaryConfig(),
        ]);
        setRules(rulesData || []);
        setDailySummary(summaryData || null);
      } catch {
        // Errors handled by hook
      } finally {
        setLoading(false);
      }
    };
    if (agentId) load();
  }, [agentId, loadNotificationRules, loadDailySummaryConfig]);

  // Create / Update rule
  const handleSaveRule = useCallback(async (formData) => {
    try {
      if (editingRule?.id) {
        await updateNotificationRule(agentId, editingRule.id, formData);
      } else {
        await createNotificationRule(agentId, formData);
      }
      const updated = await loadNotificationRules(agentId);
      setRules(updated || []);
      Swal.fire({
        icon: 'success',
        title: 'Regra salva!',
        timer: 1500,
        showConfirmButton: false,
      });
    } catch {
      // Errors handled by hook
    }
  }, [agentId, editingRule, createNotificationRule, updateNotificationRule, loadNotificationRules]);

  // Toggle enabled
  const handleToggle = useCallback(async (rule, enabled) => {
    try {
      await updateNotificationRule(agentId, rule.id, { is_enabled: enabled });
      const updated = await loadNotificationRules(agentId);
      setRules(updated || []);
    } catch {
      // Errors handled by hook
    }
  }, [agentId, updateNotificationRule, loadNotificationRules]);

  // Delete rule
  const handleDelete = useCallback(async (rule) => {
    const result = await Swal.fire({
      title: 'Excluir regra?',
      text: `A regra "${rule.name}" será removida permanentemente.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      confirmButtonText: 'Excluir',
      cancelButtonText: 'Cancelar',
    });
    if (result.isConfirmed) {
      try {
        await deleteNotificationRule(agentId, rule.id);
        const updated = await loadNotificationRules(agentId);
        setRules(updated || []);
        Swal.fire({ icon: 'success', title: 'Regra excluída!', timer: 1500, showConfirmButton: false });
      } catch {
        // Errors handled by hook
      }
    }
  }, [agentId, deleteNotificationRule, loadNotificationRules]);

  // Save daily summary
  const handleSaveDailySummary = useCallback(async (formData) => {
    try {
      const saved = await saveDailySummaryConfig(formData);
      setDailySummary(saved);
      Swal.fire({
        icon: 'success',
        title: 'Resumo diário salvo!',
        timer: 1500,
        showConfirmButton: false,
      });
    } catch {
      // Errors handled by hook
    }
  }, [saveDailySummaryConfig]);

  // Open modal
  const openNewRule = () => {
    setEditingRule(null);
    setShowModal(true);
  };

  const openEditRule = (rule) => {
    setEditingRule(rule);
    setShowModal(true);
  };

  if (loading) {
    return (
      <div className="text-center p-5">
        <Spinner animation="border" variant="primary" />
      </div>
    );
  }

  return (
    <div className="tab-content-wrapper">
      <Alert variant="info" className="mb-4">
        <IconInfoCircle size={18} className="me-2" />
        Configure regras de notificação ao proprietário. Cada regra define quando e como o agente deve notificar você sobre eventos da conversa.
      </Alert>

      {/* Add Rule Button */}
      <div className="d-flex justify-content-end mb-3">
        <Button variant="primary" onClick={openNewRule}>
          <IconPlus size={18} className="me-1" />
          Adicionar Regra
        </Button>
      </div>

      {/* Rules List */}
      {rules.length === 0 ? (
        <div className="empty-rules">
          <IconBell size={48} />
          <h6>Nenhuma regra configurada</h6>
          <p>Clique em "Adicionar Regra" para criar sua primeira regra de notificação.</p>
        </div>
      ) : (
        <div className="rules-list">
          {rules.map((rule) => (
            <RuleCard
              key={rule.id}
              rule={rule}
              onEdit={openEditRule}
              onDelete={handleDelete}
              onToggle={handleToggle}
            />
          ))}
        </div>
      )}

      {/* Daily Summary */}
      {isProPlan ? (
        <DailySummarySection config={dailySummary} onSave={handleSaveDailySummary} />
      ) : (
        <div
          className="daily-summary-section daily-summary-section--locked"
          onClick={() => openUpgradeModal({
            featureName: 'Resumo Diário',
            featureDescription: 'Receba um resumo diário com as atividades do seu agente por WhatsApp ou email.',
            featureIcon: IconClock,
          })}
          style={{ cursor: 'pointer' }}
        >
          <h5 className="mb-3" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <IconClock size={20} />
            Resumo Diário
            <ProBadge inline />
          </h5>
          <Card style={{ opacity: 0.5, pointerEvents: 'none' }}>
            <Card.Body>
              <Form.Check
                type="switch"
                id="daily-summary-toggle-locked"
                label="Enviar resumo diário das atividades"
                checked={false}
                disabled
                className="mb-0"
              />
            </Card.Body>
          </Card>
        </div>
      )}

      {/* Rule Editor Modal */}
      <RuleEditorModal
        show={showModal}
        onHide={() => setShowModal(false)}
        rule={editingRule}
        onSave={handleSaveRule}
      />
      <UpgradeModal
        show={upgradeModal.show}
        onClose={closeUpgradeModal}
        featureName={upgradeModal.featureName}
        featureDescription={upgradeModal.featureDescription}
        featureIcon={upgradeModal.featureIcon}
        featureGradient={upgradeModal.featureGradient}
      />
    </div>
  );
};

export default NotificationsConfigTab;
